import React, { useState, useEffect } from 'react';
import { 
  addMemberToOrganization, 
  getOrganizationMembers,
  updateMemberRole,
  removeMemberFromOrganization 
} from '../../../services/organizationService';
import { useUser } from '../../../context/UserContext';
import { useOrganization } from '../../../context/OrganizationContext';

const Members = ({ organizationData, organizationId }) => {
  const { userInfo } = useUser();
  const { userRole } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [members, setMembers] = useState([]);
  const [owner, setOwner] = useState(null);
  const [editingMemberId, setEditingMemberId] = useState(null); // Track which member is being edited
  const [editingRole, setEditingRole] = useState(''); // Track the role being edited

  // Load members on component mount
  useEffect(() => {
    if (organizationId) {
      loadMembers();
    }
  }, [organizationId]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const response = await getOrganizationMembers(organizationId);
      
      setOwner(response.data.data.owner);
      setMembers(response.data.data.members);
    } catch (error) {
      // Error loading members
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) return;

    setLoading(true);
    try {
      await addMemberToOrganization(organizationId, inviteEmail, inviteRole);
      
      // Refresh the members list
      await loadMembers();
      
      // Reset form
      setInviteEmail('');
      setInviteRole('viewer');
      setShowInviteModal(false);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to invite member');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    // Prevent user from removing themselves
    if (memberId === userInfo?._id) {
      alert('You cannot remove yourself from the organization. Ask another admin to remove you.');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      await removeMemberFromOrganization(organizationId, memberId);
      await loadMembers(); // Refresh list
    } catch (error) {
      alert('Failed to remove member from organization');
    }
  };

  const handleStartEditRole = (member) => {
    setEditingMemberId(member.id);
    setEditingRole(member.role);
  };

  const handleSaveRole = async (memberId) => {
    if (!editingRole) return;

    try {
      await updateMemberRole(organizationId, memberId, editingRole);
      await loadMembers(); // Refresh list
      setEditingMemberId(null);
      setEditingRole('');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update member role');
    }
  };

  const handleCancelEdit = () => {
    setEditingMemberId(null);
    setEditingRole('');
  };

  const orgName = organizationData?.name || organizationData?.agency_name || organizationData?.organization_name || 'Unknown Organization';
  
  // Combine owner and members for display
  const allMembers = owner ? [owner, ...members] : members;

  // Check permissions
  const canInviteMembers = userRole === 'owner' || userRole === 'admin' || userRole === 'analyst' || userRole === 'viewer';
  const isAdmin = userRole === 'owner' || userRole === 'admin';
  const canRemoveMembers = isAdmin; // Only admins can remove members
  const canEditRoles = isAdmin; // Only admins can edit roles

  return (
    <div>
      {/* Page Title Section */}
      <div className="flex items-center justify-between mb-6 bg-white p-6 rounded-lg shadow-lg">
        <div>
          <h2 className="text-2xl font-bold mb-0.5 text-black">Team Members</h2>
          <p className="text-base font-normal text-gray-500 m-0">Members of {orgName}</p>
        </div>
        <div>
          <button 
            className="flex items-center h-10 px-4 py-2 bg-brand-blue-dark border-none text-white rounded-lg font-medium text-sm cursor-pointer transition-all duration-200 shadow-sm hover:bg-opacity-90 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setShowInviteModal(true)}
            disabled={loading || !canInviteMembers}
            title={!canInviteMembers ? 'Only admins can invite members' : 'Invite new member'}
          >
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
            </svg>
            {loading ? 'Loading...' : 'Invite'}
          </button>
        </div>
      </div>

      {/* Team Members List */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-0">
          {allMembers.length >= 1 && allMembers.map((member) => {
            if (!member || !member.id) return null;
            
            const isOwner = member.role === 'owner';
            const isCurrentUser = member.id === userInfo?._id;
            const isMemberAdmin = member.role === 'admin';
            const isEditingThisMember = editingMemberId === member.id;
            
            return (
            <div className="flex items-center p-4 border-b border-gray-100 hover:bg-gray-50 last:border-b-0" key={member.id}>
              {/* Member Avatar */}
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 rounded-full bg-brand-blue-dark flex items-center justify-center text-white font-semibold text-base overflow-hidden">
                  {member.profilePictureUrl ? (
                    <img 
                      src={member.profilePictureUrl} 
                      alt={`${member.firstName || ''} ${member.lastName || ''}`}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <span>
                      {member.firstName 
                        ? member.firstName.charAt(0).toUpperCase()
                        : member.email?.charAt(0).toUpperCase() || 'M'
                      }
                    </span>
                  )}
                </div>
              </div>
              
              {/* Member Details */}
              <div className="flex-1 min-w-0">
                <div className="text-black font-bold text-base leading-6 truncate">
                  {member.firstName && member.lastName 
                    ? `${member.firstName} ${member.lastName}` 
                    : member.email || 'Unknown User'}
                  {isOwner && <span className="ml-2 text-xs bg-brand-pink text-white px-2 py-0.5 rounded">Owner</span>}
                  {isMemberAdmin && !isOwner && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Admin</span>}
                  {isCurrentUser && !isOwner && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">You</span>}
                </div>
                
                <div className="text-gray-500 text-sm leading-5 truncate">
                  {member.email || 'No email'}
                </div>

                {/* Role Section - Now Editable for Admins */}
                <div className="flex items-center mt-2 gap-2">
                  {!isOwner && canEditRoles && isEditingThisMember ? (
                    // Edit Mode - Larger dropdown and theme buttons
                    <div className="flex items-center gap-3">
                      <span className="text-gray-700 text-sm font-medium">Role:</span>
                      <select
                        value={editingRole}
                        onChange={(e) => setEditingRole(e.target.value)}
                        className="px-3 py-2 text-sm font-medium border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-pink focus:border-brand-pink bg-background text-foreground min-w-[140px]"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="analyst">Analyst</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button
                        onClick={() => handleSaveRole(member.id)}
                        className="px-3 py-1.5 bg-brand-pink text-white text-xs font-medium rounded-lg hover:bg-brand-pink/90 transition-colors shadow-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1.5 bg-background text-grey border border-border text-xs font-medium rounded-lg hover:bg-background hover:text-foreground transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-center gap-2">
                      <div className="text-gray-700 text-sm">
                        Role: {member.role ? member.role.charAt(0).toUpperCase() + member.role.slice(1) : 'No role assigned'}
                      </div>
                      {!isOwner && !isCurrentUser && canEditRoles && (
                        <button
                          onClick={() => handleStartEditRole(member)}
                          className="text-xs text-brand-pink hover:text-brand-pink/80 font-medium transition-colors"
                          title="Edit role"
                        >
                          Edit
                        </button>
                      )}
                      {(isOwner || (isMemberAdmin && !canEditRoles)) && (
                        <span className="ml-2 text-xs text-gray-500">(Contact admin to change)</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Menu - Only show for non-owners, non-admins, non-current-user, and only if user is admin */}
              {!isOwner && !isMemberAdmin && !isCurrentUser && canRemoveMembers && (
                <div className="flex-shrink-0 ml-4 relative">
                  <button 
                    className="cursor-pointer hover:opacity-75 transition-opacity p-2"
                    onClick={() => handleRemoveMember(member.id)}
                    title="Remove member"
                  >
                    <svg className="w-5 h-5 text-red-400 hover:text-red-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              )}
              
              {/* Show "You" indicator for current user */}
              {isCurrentUser && !isOwner && !canRemoveMembers && (
                <div className="flex-shrink-0 ml-4">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">You</span>
                </div>
              )}
              
              {/* Show lock icon for admins/owner if current user can't edit */}
              {(isMemberAdmin || isOwner) && !canEditRoles && (
                <div className="flex-shrink-0 ml-4">
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                  </svg>
                </div>
              )}
            </div>
            );
          })}
          
          {allMembers.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 font-medium p-0 m-0">No team members found.</p>
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 overflow-hidden" style={{ zIndex: 9998 }}>
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowInviteModal(false)}
            style={{ zIndex: 9998 }}
          />
          
          <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-auto">
              <div className="px-6 pt-6 pb-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-black m-0">Send Invite</h2>
                    <p className="text-gray-600 text-sm mt-1 m-0">Add members to {orgName}</p>
                  </div>
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="email"
                      className="form-input flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter member email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                    <button 
                      type="button" 
                      className="w-10 h-10 bg-brand-blue-dark text-white rounded-lg hover:bg-opacity-90 transition-colors duration-200 flex items-center justify-center font-bold text-lg disabled:opacity-50"
                      onClick={handleInviteMember}
                      disabled={loading || !inviteEmail.trim()}
                    >
                      {loading ? '...' : '+'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">Invite as:</span>
                  <select 
                    className="form-select px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                  >
                    <option value="viewer">Viewer</option>
                    {isAdmin && <option value="analyst">Analyst</option>}
                    {isAdmin && <option value="admin">Admin</option>}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {isAdmin 
                    ? 'As an admin, you can invite members with any role.'
                    : 'You can only invite viewers. Contact an admin to add analysts or admins.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Members;
